import React from 'react';
import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { PinnacleTextField } from '../../../../../components/PinnacleTextField';
import { Program } from '../../../../../api/interfaces/Program';
import { ReferenceFormRequest } from '../../../../../api/interfaces/Reference';
import { FormData } from '../../../hooks/useReferenceForm';
import { E_FIELD_REQUIRED } from '../../../../../utils/constants';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';

interface ReferenceInformationProps {
    programs: Program[];
    touchedFields: Record<keyof FormData, boolean>;
    onBlur: (field: keyof FormData) => void;
    isSubmitting: boolean;
    formData: ReferenceFormRequest;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleUploadClick: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    selectedImage: File | null;

}

const ReferenceInformation: React.FC<ReferenceInformationProps> = ({
    programs,
    touchedFields,
    onBlur,
    isSubmitting,
    formData,
    onChange,
    handleUploadClick,
    fileInputRef,
    handleFileChange,
    selectedImage
}) => {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <PinnacleTextField
                select
                label="Program"
                name="programId"
                value={formData.programId}
                onChange={onChange}
                fullWidth
                onBlur={() => onBlur('programId')}
                error={touchedFields.programId && !formData.programId.trim()}
                helperText={
                    touchedFields.programId && !formData.programId.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            >
                {programs.map((program: Program) => (
                    <MenuItem key={program._id} value={program._id}>
                        <Typography
                            noWrap
                            sx={{
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                            }}
                        >
                            {program.name}
                        </Typography>
                    </MenuItem>
                ))}
            </PinnacleTextField>

            <PinnacleTextField
                label="Title"
                name="title"
                value={formData.title}
                onChange={onChange}
                fullWidth
                onBlur={() => onBlur('title')}
                error={touchedFields.title && !formData.title.trim()}
                helperText={
                    touchedFields.title && !formData.title.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />

            <PinnacleTextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={onChange}
                multiline
                rows={5}
                fullWidth
                onBlur={() => onBlur('description')}
                error={touchedFields.description && !formData.description.trim()}
                helperText={
                    touchedFields.description && !formData.description.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />

            <Stack spacing={2} sx={{ mb: 2 }}>
                <Box
                    onClick={handleUploadClick}
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#f9f9f9',
                        },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        {selectedImage ? (
                            <>
                                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={500}
                                        noWrap
                                        sx={{
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            maxWidth: 200
                                        }}
                                    >
                                        {selectedImage.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Click to change thumbnail
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <>
                                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        Upload Thumbnail
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Browse file to upload
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Stack>

                    <input
                        type="file"
                        accept="image/jpeg, image/jpg, image/png, image/svg+xml"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                </Box>
            </Stack>
        </Box>
    );
};


export default ReferenceInformation;
